import React, { useMemo, useRef } from "react";
import { AutocompleteInput, ChoicesContext, ReferenceInput, useChoicesContext, required } from "react-admin";
import { employeeRequests } from "api/employeeApi";

const byNameBg = (a, b) => (a?.nameBg ?? "").localeCompare(b?.nameBg ?? "", "bg");

// Inline here and AutocompleteInput would wipe the typed search on every re-render.
const getSubstituteName = record => record?.nameBg;

// Sort the context, not a `choices` prop — that would replace the ReferenceInput's fetching.
const SortedChoices = ({ selectedId, children }) => {
  const context = useChoicesContext();
  const { allChoices, availableChoices } = context;

  const selectedChoice = useMemo(
    () => (selectedId ? (allChoices ?? []).find(choice => choice?.employeeId === selectedId) : null),
    [allChoices, selectedId],
  );

  // No `id` in the payload, so react-admin cannot re-fetch the selection — keep our own copy.
  const lastSelectedChoice = useRef(null);
  if (!selectedId) lastSelectedChoice.current = null;
  else if (selectedChoice) lastSelectedChoice.current = selectedChoice;

  const sortedAll = useMemo(() => {
    const choices = [...(allChoices ?? [])];
    const sticky = lastSelectedChoice.current;
    if (sticky && !selectedChoice && sticky.employeeId === selectedId) choices.push(sticky);
    return choices.sort(byNameBg);
  }, [allChoices, selectedChoice, selectedId]);
  const sortedAvailable = useMemo(() => [...(availableChoices ?? [])].sort(byNameBg), [availableChoices]);
  const sortedContext = useMemo(
    () => ({ ...context, allChoices: sortedAll, availableChoices: sortedAvailable }),
    [context, sortedAll, sortedAvailable],
  );

  return <ChoicesContext.Provider value={sortedContext}>{children}</ChoicesContext.Provider>;
};

const SubstituteAutocomplete = ({ isRequired, selectedId }) => {
  const { allChoices } = useChoicesContext();
  // A substitute already saved on the request can come back absent — keep it visible, but greyed.
  const isSelectedAbsent = allChoices?.some(
    choice => choice?.employeeId === selectedId && choice?.isAbsent === true,
  );

  return (
    <AutocompleteInput
      sx={{ m: 0 }}
      label="Заместник"
      validate={isRequired ? required() : undefined}
      optionText={getSubstituteName}
      optionValue="employeeId"
      filterToQuery={q => (q?.length > 2 ? { EmployeeName: q } : {})}
      getOptionDisabled={choice => choice?.isAbsent === true}
      TextFieldProps={isSelectedAbsent ? { sx: { "& input": { color: "text.disabled" } } } : undefined}
    />
  );
};

const SubstituteInput = ({ employeeId, isRequired, selectedId, dateFrom, dateTo }) => {
  // Candidates are scoped to the period, so there is nothing to ask the backend for without it.
  if (!dateFrom || !dateTo)
    return (
      <AutocompleteInput
        sx={{ m: 0 }}
        source="substituteEmployeeId"
        label="Заместник"
        helperText="Изберете период"
        choices={[]}
        disabled
      />
    );

  return (
    <ReferenceInput
      key={`substitute-${employeeId ?? "self"}-${dateFrom}-${dateTo}`}
      source="substituteEmployeeId"
      reference={employeeRequests.getSubstitutionsSelect(dateFrom, dateTo)}
      perPage={100}
      filter={employeeId ? { employeeId } : {}}
    >
      <SortedChoices selectedId={selectedId}>
        <SubstituteAutocomplete
          isRequired={isRequired}
          selectedId={selectedId}
        />
      </SortedChoices>
    </ReferenceInput>
  );
};

export default SubstituteInput;
